import { useState, useEffect } from 'react';
import { MdCalendarToday } from 'react-icons/md';
import ModalElement from '../../../Common/ModalElement';
import ButtonElement from '../../../Common/ButtonElement';
import MultiSelectDropdown from '../../../Common/MultiChoiceElement';
import { taskManagementApi } from '../../../actions/taskManagement';
import { useApiRequest } from '../../../hooks/useApiRequest';
import { capitalizeWord, formatDate } from '../../../utils';
import SearchableDropdown from '../../../Common/SearchDropdown';
import InputElement from '../../../Common/InputElement';
import DatePicker from 'react-datepicker';

const findUniqueElement = (arr1, arr2) => {
    // Determine the change type based on array lengths
    const length1 = arr1.length;
    const length2 = arr2.length;
    let change = null;

    if (length1 > length2) change = 'DELETE';
    else if (length2 > length1) change = 'POST';
    else return { element: null, change };

    // Determine the longer and shorter array
    const longerArray = length1 > length2 ? arr1 : arr2;
    const shorterArray = length1 > length2 ? arr2 : arr1;

    // Use a stringified version for object comparison to check for content equality
    const shorterSet = new Set(shorterArray.map(item => typeof item === 'object' && item !== null ? JSON.stringify(item) : item));

    for (const element of longerArray) {
        const stringifiedElement = typeof element === 'object' && element !== null ? JSON.stringify(element) : element;
        if (!shorterSet.has(stringifiedElement)) {
            return { element, change };
        }
    }

    // This should not be reached if the assumption holds
    return { element: null, change: null };
};

const TicketDetails = ({ show, hideModal, ticket, onSuccess, taskStatuses = [], ccMemberOptions = [] }) => {
    const { request, loading } = useApiRequest();
    const [state, setState] = useState({
        createdBy: "",
        newComment: "",
        selectedStatus: "",
        selectedAssignee: "",
        selectAssigneeId: null,
        comments: [],
        selectedCcMembers: [],
        assigneeOptions: [],
        newCommentErr: null,
        dueDate : null
    });

    const { createdBy, newComment, selectedStatus, selectedAssignee, selectAssigneeId, selectedCcMembers, newCommentErr, comments , dueDate} = state;

    const ticketDetails = async () => {
        // Load Title and Comments
        await request({
            method: "GET",
            url: `${taskManagementApi.getTaskById}/${ticket.task_id}/details`,
            showToast: false,
            onSuccess: (response) => {
                const { Comments, TaskStatus, assignee_id, assignee, Creator, due_date, TaskCcMembers } = response?.data ?? {};
                const ccMem = [];
                TaskCcMembers?.map(member => {
                    const cc = ccMemberOptions?.find(ccMember => ccMember?.id == member.user_id);
                    if(cc) ccMem.push(cc);
                });
                setState(prev => ({
                    ...prev,
                    comments: Comments ?? [],
                    selectedCcMembers: ccMem,
                    selectedStatus: TaskStatus?.status_name ?? "",
                    selectAssigneeId: assignee_id || null,
                    selectedAssignee: assignee?.name ?? "",
                    createdBy: Creator?.name ?? "",
                    dueDate: due_date ? due_date.split('T')[0] : ""
                }));
            }
        });
    }

    const handleStatusChange = async (newStatus) => {
        if (!newStatus || newStatus?.name === selectedStatus) return;
        const statusId = newStatus?.id;

        await request({
            method: "PUT",
            url: `${taskManagementApi.updateTask}/${ticket.task_id}`,
            body: { status: statusId },
            showToast: true,
            onSuccess: () => {
                // Update local state with the new status
                const statusName = taskStatuses.find(s => s.id === statusId)?.name || "";
                setState(prev => ({ ...prev, selectedStatus: statusName }));
                // Inform parent to merge locally
                onSuccess({ task_id: ticket.task_id, status: statusId, TaskStatus: { status_id: statusId, status_name: statusName, type: 'Task' } });
            }
        });
    }


    const handleAssigneeChange = async (newAssignee) => {
        if (!newAssignee || newAssignee?.id === selectAssigneeId) return;
        const assigneeId = newAssignee?.id;

        await request({
            method: "PUT",
            url: `${taskManagementApi.updateTask}/${ticket.task_id}`,
            body: { assignee_id: assigneeId },
            showToast: true,
            onSuccess: (response) => {
                // Update local state with the new assignee
                const assigneeName = response?.data?.task?.assignee?.name ?? "";
                setState(prev => ({ ...prev, selectAssigneeId: assigneeId, selectedAssignee: assigneeName }));
                // Inform parent to merge locally
                onSuccess({ task_id: ticket.task_id, assignee_id: assigneeId, assignee: { user_id: assigneeId, name: assigneeName } });
            }
        });
    };

    const handleCcMembersChange = async (selectedMembers) => {
        const uniqueElement = findUniqueElement(selectedCcMembers, selectedMembers);
        setState(prev => ({ ...prev, selectedCcMembers: selectedMembers }));
        const method = uniqueElement?.change;
        const oddMember = uniqueElement?.element?.id;
        if (method && oddMember) {
            await request({
                method,
                url: `${taskManagementApi.ccMembers}/${ticket.task_id}/cc${method == "DELETE" ? `/${oddMember}` : ''}`,
                body: method == "DELETE" ? null : { user_id: oddMember },
                showToast: false
            });
        }
        onSuccess({ task_id: ticket.task_id, TaskCcMembers: selectedMembers.map(m => ({ id: m.id, user_id: m.id })) });
    }

    const handleDueDateChange = async (newDueDate) => {
        if (!newDueDate) return;
        
        await request({
            method: "PUT",
            url: `${taskManagementApi.updateTask}/${ticket.task_id}`,
            body: { due_date: newDueDate },
            showToast: true,
            successMessage: "Due date updated successfully!",
            onSuccess: () => {
                setState(prev => ({ ...prev, dueDate: newDueDate }));
                onSuccess({ task_id: ticket.task_id, due_date: newDueDate });
            }
        });
    }

    const handleAddComment = async () => {
        if (!newComment.trim()) {
            setState(prev => ({ ...prev, newCommentErr: "Please enter value." }));
            return;
        }

        await request({
            method: "POST",
            url: `${taskManagementApi.createTaskComment}/${ticket.task_id}/comments`,
            body: { comment_text: newComment },
            showToast: true,
            onSuccess: () => {
                onSuccess();
                setTimeout(hideModal, 1000);
            },
        });
    };

    // Load ticket data when component mounts
    useEffect(() => {
        if (ticket?.task_id) ticketDetails(ticket.task_id);
    }, []);

    return (
        <div>
            <ModalElement
                show={show}
                onHide={hideModal}
                title=""
                size="lg"
            >
                <div className='ticket-modal create-task-form text-start'>
                    <h3 className='fw-bold'>{ticket?.title ?? ""}</h3>
                    <p style={{ color: "#738196", fontWeight: "500" }}>
                        Created by {capitalizeWord(createdBy || "")}
                        <span style={{ marginLeft: "8px" }}>
                            {formatDate(ticket?.created_at ?? "")}
                        </span>
                    </p>


                    {/* Status section */}
                    <div className='d-flex flex-row align-items-center'>
                        <div className='text-start'>
                            <SearchableDropdown
                                label="Status"
                                placeholder="Select a Status"
                                value={selectedStatus}
                                onSelect={(selected) => handleStatusChange(selected)}
                                options={selectedStatus !== "To Do" ? taskStatuses.filter((status) => status.status_name !== "To Do") : taskStatuses}
                            />
                        </div>
                        <div className='d-flex align-items-center justify-content-center mt-4 px-1 py-1'>
                            <span style={{
                                backgroundColor: ticket.priority === 'Low'
                                    ? '#81C784'   // Mint
                                    : ticket.priority === 'Medium'
                                        ? '#FFCC80'   // Peach
                                        : ticket.priority === 'High'
                                            ? '#E57373'   // Raspberry
                                            : '#6c757d'   // Default Gray
                            }}

                                className={`badge fw-bolder`}>
                                {ticket.priority}
                            </span>
                        </div>
                        <div className='d-flex align-items-center mt-3 ms-auto'>
                            <span style={{ color: "#738196" }}>
                                <InputElement
                                    label={"Due Date"}
                                    type="date"
                                    placeholder="Pick a date"
                                    name="dueDate"
                                    value={dueDate || ""}
                                    onChange={(e) => handleDueDateChange(e.target.value)}
                                    disabled={loading}
                                    isRequired={false}
                                />
                            </span>
                        </div>
                    </div>

                    {/* Assignee Section */}
                    <div className="d-flex mt-3 flex-row align-items-center justify-content-between">
                        {/* Left side */}
                        <div className="d-flex align-items-center">
                            <h6 className="mb-0 fw-bolder">Assignee:</h6>
                            <span style={{ backgroundColor: '#f1f4f7' }} className="ms-2 text-muted badge">
                                {selectedAssignee?.charAt(0)?.toUpperCase() || "U"}
                            </span>
                            <span className="ms-2">{capitalizeWord(selectedAssignee ?? "")}</span>
                        </div>

                        {/* Right side */}
                        <div>
                            <SearchableDropdown
                                label="Assignee"
                                placeholder="Select an assignee"
                                value={selectedAssignee}
                                onSelect={(selected) => handleAssigneeChange(selected)}
                                options={ccMemberOptions}
                            />
                        </div>
                    </div>

                    {/* CC Members section with MultiSelectDropdown */}
                    <div className="mt-3 text-start">
                        <h6 className="fw-bolder mb-2">CC Members:</h6>
                        <MultiSelectDropdown
                            placeholder="Select CC members"
                            options={ccMemberOptions}
                            values={selectedCcMembers}
                            onSelect={handleCcMembersChange}
                            disabled={loading}
                            isRequired={false}
                        />
                    </div>

                    {/* Description */}
                    <div className='d-flex text-start flex-column mt-4'>
                        <label className='fw-bold'>Description</label>
                        <p style={{ color: "#738196" }}>{ticket?.description ?? ""}</p>
                    </div>

                    <div className="d-flex flex-column mb-2">
                        <div ><h6 style={{ color: "#212529" }} className='fw-bolder'>Comments</h6></div>
                        {/* Comments section */}
                        <InputElement
                            isTextArea={true}
                            label={""}
                            placeholder={"Add a comment..."}
                            name={"newComment"}
                            value={newComment}
                            error={newCommentErr}
                            onChange={(e) => {
                                setState(prev => ({ ...prev, newComment: e.target.value, newCommentErr: null }))
                            }}
                        />
                        <div>
                            <ButtonElement
                                text="Add Comment"
                                cssClass="small-radius mt-2 mb-4"
                                variant="btn-primary"
                                onClick={handleAddComment}
                                disabled={loading}
                            />
                        </div>
                        {comments.map((comment, idx) => (
                            <div
                                key={idx}
                                className="rounded-3 d-flex p-2 flex-row align-items-center mb-2"
                                style={{
                                    backgroundColor: "#f6f9fe",
                                    border: "1px solid #d2e0fd",
                                }}>
                                <h4 className="mb-0">
                                    <span style={{ backgroundColor: "#f1f4f7" }}
                                        className="ms-2 text-muted badge">
                                        {comment?.User?.name?.charAt(0)?.toUpperCase() || "U"}
                                    </span>
                                </h4>

                                <div className="ms-3">
                                    <div style={{ color: "#344255", fontWeight: 600 }} className='fs-6 fw-bolder'>
                                        {capitalizeWord(comment.User?.name || "Unknown")}
                                        <span className='ms-2' style={{ color: "#5b6676", fontWeight: "400", fontSize: "13px" }}>{formatDate(comment?.created_at ?? "", "DD-MM-YYYY hh:mm A")}</span>
                                    </div>
                                    <div><h6 style={{ color: "#5b6676", fontWeight: "400", fontSize: "14px" }}>{comment?.comment_text || "No message"}</h6></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </ModalElement>
        </div>
    )
}

export default TicketDetails